/**
 * last-selfie — cross-session cache of the user's most recent selfie upload.
 *
 * Stores in localStorage so the AvatarFlow upload step can pre-populate with
 * the previously uploaded selfie ("use this one or choose another").
 *
 * The dataUrl is a small (≤256×256) preview to keep the localStorage footprint
 * well below the 5 MB browser quota — the full image lives in R2 referenced by
 * r2Key, the dataUrl is just a thumbnail.
 *
 * The cached r2Key is presented to the backend at invoice/payment time. If the
 * backend rejects it (expired, wrong user, etc.), the AvatarFlow falls back to
 * re-uploading via the file picker.
 */
const STORAGE_KEY = 'calestea.lastSelfie.v1';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const THUMB_MAX = 256;

export interface LastSelfie {
  r2Key: string;
  dataUrl: string;
  uploadedAt: number;
}

export function getLastSelfie(): LastSelfie | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastSelfie>;
    if (
      typeof parsed.r2Key !== 'string' ||
      typeof parsed.dataUrl !== 'string' ||
      typeof parsed.uploadedAt !== 'number'
    ) {
      return null;
    }
    if (Date.now() - parsed.uploadedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as LastSelfie;
  } catch {
    return null;
  }
}

export function clearLastSelfie(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

async function fileToThumbDataUrl(file: File): Promise<string> {
  const fullUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('decode_failed'));
    el.src = fullUrl;
  });

  const scale = Math.min(1, THUMB_MAX / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return fullUrl; // fallback — rare
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.78);
}

export async function saveLastSelfie(input: { r2Key: string; file: File }): Promise<void> {
  try {
    const dataUrl = await fileToThumbDataUrl(input.file);
    const payload: LastSelfie = {
      r2Key: input.r2Key,
      dataUrl,
      uploadedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage / canvas unavailable — degrade silently */
  }
}
